import {
  ExclamationCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Divider,
  Select,
  Tag,
  Typography,
  notification,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
} from '../../../../../utils/fn/medias';
import MediaThumbnail from '../mediaList/MediaThumbnail';
import {
  editionActionButtonStyle,
  editionActionsInnerStyle,
  editionActionsRowStyle,
} from './editionActionsLayout';

import { useGetMediaToQualifyDetails } from '../../../../../utils/api/medias/useGetMediaToQualifyDetails';
import { useGetMediaTags } from '../../../../../utils/api/medias/useGetMediaTags';
import { useUpdateTagsMediasToQualify } from '../../../../../utils/api/medias/useUpdateTagsMediasToQualify';
import { useUpdateStatusMediasToQualify } from '../../../../../utils/api/medias/useUpdateStatusMediasToQualify';

const { Text } = Typography;

const labelStyle = {
  display: 'block',
  marginBottom: 6,
  fontSize: 12,
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.65)',
};

const readOnlyBlockStyle = { marginBottom: 20 };

/** Titres de sections (sous les Divider) : plus gras et un peu plus grands. */
const sectionTitleStyle = {
  fontWeight: 700,
  fontSize: 15,
  letterSpacing: '0.02em',
  color: 'rgba(0, 0, 0, 0.88)',
};

const formatCreatedAt = (iso, fallbackDate) => {
  if (iso) {
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        dateStyle: 'long',
        timeStyle: 'short',
      });
    } catch {
      /* ignore */
    }
  }
  return fallbackDate || '—';
};

/**
 * Même composant d’aperçu que liste / grille (MediaThumbnail), en plus grand.
 * `key` sur le média évite toute réutilisation de nœud entre navigations.
 */
const MediaPanelPreview = ({ media }) => (
  <div
    style={{
      width: '100%',
      margin: '0 auto',
      borderRadius: 8,
      padding: 8,
      backgroundColor: '#f0f0f0',
      boxSizing: 'border-box',
    }}
  >
    <MediaThumbnail
      media={media}
      size="large"
      iconSize={48}
      style={{ borderRadius: 8 }}
    />
  </div>
);

/**
 * Panneau unique détails + édition, réutilisé tel quel pour 1 média
 * et dans l’onglet « Détails » de la sélection multiple.
 */
const MediaDetailsEdition = ({ medias }) => {
  const selectionKey = useMemo(
    () => medias.map((m) => m?.id ?? '').join(','),
    [medias]
  );

  const [previewIndex, setPreviewIndex] = useState(0);
  const prevSelectionKeyRef = useRef(selectionKey);

  const count = medias.length;

  useEffect(() => {
    const keyChanged = prevSelectionKeyRef.current !== selectionKey;
    prevSelectionKeyRef.current = selectionKey;
    const max = Math.max(0, count - 1);
    if (keyChanged) {
      setPreviewIndex(0);
    } else {
      setPreviewIndex((i) => Math.min(i, max));
    }
  }, [selectionKey, count]);
  const safeIndex =
    count === 0 ? 0 : Math.min(Math.max(0, previewIndex), count - 1);

  const currentMediaId = medias[safeIndex]?.id;
  const { media } = useGetMediaToQualifyDetails(currentMediaId);

  const [searchedTag, setSearchedTag] = useState('');
  const [debouncedSearchTag, setDebouncedSearchTag] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTag(searchedTag.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchedTag]);

  const { tags, isLoading: isTagLoading } = useGetMediaTags({
    search: debouncedSearchTag,
  });

  const handleTagsChange = (value) => {
    setSearchedTag('');
    setTagValues(value);
  };

  const tagOptions = useMemo(() => {
    return tags?.map((tag) => ({
      value: tag.name,
      label: tag.name,
    }));
  }, [tags]);

  const [tagValues, setTagValues] = useState([]);
  const [oldTagValues, setOldTagValues] = useState([]);

  useEffect(() => {
    if (!media) return;
    setOldTagValues(media.tags ?? []);
    setTagValues(media.tags ?? []);
  }, [media]);

  const { updateTagsMediasToQualify } = useUpdateTagsMediasToQualify();
  const { updateStatusMediasToQualify } = useUpdateStatusMediasToQualify();

  const [savingMedias, setSavingMedias] = useState(false);
  const [savingAndSendingMedias, setSavingAndSendingMedias] = useState(false);

  const handleSave = useCallback(
    async (oldTags, newTags) => {
      setSavingMedias(true);
      try {
        await updateTagsMediasToQualify({
          old_tags: oldTags,
          new_tags: newTags,
          medias: medias,
        });
        setOldTagValues(newTags);

        notification.success({
          message: 'Enregistrement',
          description: 'Les modifications ont été enregistrées',
          placement: 'bottomRight',
          duration: 3,
        });
      } catch (e) {
        notification.error({
          message: "Erreur d'enregistrement",
          description: `Les modifications n'ont pas pu être enregistrées : ${e}`,
          placement: 'bottomRight',
          duration: 3,
        });
      }
      setSavingMedias(false);
    },
    [medias, updateTagsMediasToQualify]
  );

  const handleSaveAndSubmit = useCallback(
    async (oldTags, newTags) => {
      setSavingAndSendingMedias(true);
      try {
        await updateTagsMediasToQualify({
          old_tags: oldTags,
          new_tags: newTags,
          medias: medias,
        });
        setOldTagValues(newTags);

        const result = await updateStatusMediasToQualify({
          status: 'to_validate',
          mediaIds: medias.map((media) => media.id),
        });

        if (result?.errors?.length > 0) {
          throw new Error(result.errors.map((e) => e.error).join('; '));
        }

        notification.success({
          message: 'Enregistrement et envoi en validation effectués',
          description: 'Les modifications ont été enregistrées',
          placement: 'bottomRight',
          duration: 3,
        });
      } catch (e) {
        notification.error({
          message: "Erreur d'enregistrement",
          description: `Les modifications n'ont pas pu être enregistrées : ${e}`,
          placement: 'bottomRight',
          duration: 3,
        });
      }
      setSavingAndSendingMedias(false);
    },
    [medias, updateTagsMediasToQualify, updateStatusMediasToQualify]
  );

  const dimensionsLabel = media?.dimensions ?? '—';
  const resolutionLabel = media?.resolution || '—';

  const showPreviewNav = count > 1;

  return (
    <div style={{ padding: '16px' }}>
      <div
        style={{
          marginLeft: -16,
          marginRight: -16,
          width: 'calc(100% + 32px)',
          marginBottom: 24,
        }}
      >
        {showPreviewNav && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 12px',
              marginBottom: 12,
            }}
          >
            <Button
              type="link"
              icon={<LeftOutlined />}
              disabled={safeIndex <= 0}
              onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
              aria-label="Média précédent"
            />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {safeIndex + 1}/{count}
            </Text>
            <Button
              type="link"
              icon={<RightOutlined />}
              disabled={safeIndex >= count - 1}
              onClick={() => setPreviewIndex((i) => Math.min(count - 1, i + 1))}
              aria-label="Média suivant"
            />
          </div>
        )}
        {media ? <MediaPanelPreview key={media.id} media={media} /> : null}
      </div>

      <div style={readOnlyBlockStyle}>
        <span style={labelStyle}>Nom du média</span>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Text>{media?.name || '—'}</Text>
          {media ? (
            <>
              <Tag
                icon={getStatusIcon(media.status)}
                color={media.statusColor || getStatusColor(media.status)}
              >
                {media.statusLabel || getStatusLabel(media.status)}
              </Tag>
            </>
          ) : null}
        </div>
      </div>

      {media?.reject_reasons && media.reject_reasons.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {media.reject_reasons
            .filter((reject_reason) => reject_reason.reason)
            .map((reject_reason, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <Alert
                  type={'error'}
                  showIcon
                  icon={
                    <ExclamationCircleOutlined
                      style={{
                        color: '#ff4d4f',
                        fontSize: 18,
                        marginTop: 2,
                      }}
                    />
                  }
                  message={
                    <span style={{ color: '#ff4d4f' }}>
                      {reject_reason.reason}
                    </span>
                  }
                  style={{
                    background: '#fff1f0',
                    borderColor: '#ffa39e',
                  }}
                />
              </div>
            ))}
        </div>
      )}

      <div style={readOnlyBlockStyle}>
        <span style={labelStyle}>Description</span>
        <Text style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
          {media?.product?.short_description || '—'}
        </Text>
      </div>

      <div style={readOnlyBlockStyle}>
        <span style={labelStyle}>Ordre</span>
        <Text style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
          {media?.image_order > 0 && media?.image_order < 100
            ? media?.image_order
            : '—'}
        </Text>
      </div>

      <div style={readOnlyBlockStyle}>
        <span style={labelStyle}>Tags</span>
        <Select
          mode="tags"
          style={{ width: '100%' }}
          placeholder="Saisir ou sélectionner des tags"
          options={tagOptions}
          value={tagValues}
          onChange={handleTagsChange}
          searchValue={searchedTag}
          onSearch={setSearchedTag}
          loading={isTagLoading}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? option?.value ?? '')
              .toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      </div>

      <Divider orientation="left" plain>
        <span style={sectionTitleStyle}>Informations techniques</span>
      </Divider>
      <div style={readOnlyBlockStyle}>
        <span style={labelStyle}>Dimensions</span>
        <Text>{dimensionsLabel}</Text>
      </div>
      <div style={{ marginBottom: 20 }}>
        <span style={labelStyle}>Résolution</span>
        <Text>{resolutionLabel}</Text>
      </div>

      <Divider orientation="left" plain>
        <span style={sectionTitleStyle}>Informations produit</span>
      </Divider>
      <div style={readOnlyBlockStyle}>
        <span style={labelStyle}>Gamme</span>
        <Text>{media?.product?.range || '—'}</Text>
      </div>
      <div style={{ marginBottom: 20 }}>
        <span style={labelStyle}>Nom du produit</span>
        <Text>{media?.product?.name || '—'}</Text>
      </div>

      <Divider orientation="left" plain>
        <span style={sectionTitleStyle}>Métadonnées</span>
      </Divider>
      <div style={readOnlyBlockStyle}>
        <span style={labelStyle}>Date de création</span>
        <Text>{formatCreatedAt(media?.created_at, media?.created_at)}</Text>
      </div>
      <div style={{ marginBottom: 20 }}>
        <span style={labelStyle}>Auteur</span>
        <Text>{media?.created_by || '—'}</Text>
      </div>

      <Divider style={{ margin: '24px 0 16px' }} />

      <div style={editionActionsRowStyle}>
        <div style={editionActionsInnerStyle}>
          <Button
            type="primary"
            block
            style={editionActionButtonStyle}
            onClick={() => handleSave(oldTagValues, tagValues)}
            loading={savingMedias}
            disabled={savingAndSendingMedias}
          >
            {!savingMedias
              ? 'Enregistrer les modifications'
              : 'Enregistrement en cours'}
          </Button>
          <Button
            block
            style={editionActionButtonStyle}
            onClick={() => handleSaveAndSubmit(oldTagValues, tagValues)}
            loading={savingAndSendingMedias}
            disabled={media?.status === 'rejected' || savingMedias}
          >
            {!savingAndSendingMedias
              ? 'Enregistrer et envoyer en validation'
              : 'Enregistrement et envoi en cours'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MediaDetailsEdition;
